import {
  ApartmentOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  NumberOutlined,
  ShoppingOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {Modal, Table, Tag} from 'antd';
import PropTypes from 'prop-types';
import {useEffect} from 'react';

const InfoRow = ({icon, label, value, className = ''}) => (
  <div className={`flex items-start gap-2 ${className}`}>
    <span className={'text-primary-700 mt-0.5 flex-shrink-0'}>{icon}</span>
    <div className={'min-w-0'}>
      <p className={'font-medium uppercase tracking-wide leading-none mb-0.5 text-xs text-gray-500'}>{label}</p>
      <p className={'font-medium break-words text-gray-800'}>{value || '—'}</p>
    </div>
  </div>
);
InfoRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
};

const ProductionDetails = ({viewModal, setViewModal}) => {
  const record = viewModal.record;

  useEffect(() => {
    if (!viewModal.visible) {return;}
  }, [viewModal.visible]);

  if (!record) {return null;}

  const isActive = record?.status === 'ACTIVE';
  const ingredients = record?.ingredients || [];
  const finalProduct = record?.finalProduct || {};

  const formattedDate = record.productionDate
    ? new Date(record.productionDate).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    : '—';

  const ingredientColumns = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      render: val => <span className={'font-medium text-gray-800'}>{val || '—'}</span>,
    },
  
    {
      title: 'Required Qty',
      dataIndex: 'requiredQuantity',
      key: 'requiredQuantity',
      align: 'right',
      render: (val, row) => (
        <span className={'font-semibold text-blue-600'}>
          {val ?? '—'} <span className={'text-gray-400 font-normal text-xs'}>{row.unit || ''}</span>
        </span>
      ),
    },
  ];

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3'}>
          <span className={'text-lg font-bold text-gray-800'}>Production Details</span>
         
        </div>
      )}
      open={viewModal.visible}
      footer={null}
      width={860}
      onCancel={() => setViewModal({visible: false, record: null})}
      destroyOnClose
      styles={{
        body: {padding: '0', background: '#f8fafc'},
        header: {borderBottom: '1px solid #e2e8f0', paddingBottom: 12},
      }}
    >
      <div className={'bg-slate-50 p-5 md:p-6 space-y-5 rounded-b-lg'}>

        {/* HEADER: Lot + Date */}
        <div className={'bg-white rounded-lg flex flex-wrap justify-between items-start shadow-sm border border-primary-100 p-5 gap-4'}>
          <div>
            <p className={'text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1'}>Lot Number</p>
            <h2 className={'text-2xl font-bold text-primary-900 leading-tight'}>
              {record.lotNumber || '—'}
            </h2>
          </div>

          <div className={'flex flex-col gap-3'}>
           
            <InfoRow
              icon={<CalendarOutlined className={'text-xl text-black'} />}
              label={'Production Date'}
              value={formattedDate}
            />
          </div>
        </div>

        {/* FINAL PRODUCT */}
        {(finalProduct.productName || finalProduct.productId) && (
          <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5'}>
            <h3 className={'font-bold text-base text-gray-700 flex items-center gap-2 mb-4'}>
              <ShoppingOutlined className={'text-blue-500'} /> Final Product
            </h3>
            <div className={'grid grid-cols-2 md:grid-cols-3 gap-4'}>
              <div className={'bg-slate-50 rounded-md p-3'}>
                <p className={'text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1'}>Product Name</p>
                <p className={'font-bold text-gray-800 text-sm'}>{finalProduct.productName || '—'}</p>
              </div>
              
              <div className={'bg-primary-50 rounded-md p-3'}>
                <p className={'text-xs text-primary-400 font-semibold uppercase tracking-wide mb-1'}>Quantity</p>
                <p className={'text-2xl font-bold text-primary-700 leading-none'}>
                  {finalProduct.quantity ?? '—'}
                </p>
              </div>
              <div className={'bg-slate-50 rounded-md p-3'}>
                <p className={'text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1'}>Unit</p>
                <p className={'font-bold text-gray-800 text-sm'}>{finalProduct.unit || '—'}</p>
              </div>
            </div>
          </div>
        )}

        {/* INGREDIENTS */}
        {ingredients.length > 0 && (
          <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5'}>
            <h3 className={'font-bold text-base text-gray-700 flex items-center gap-2 mb-4'}>
              <ExperimentOutlined className={'text-blue-500'} /> Ingredients
              <Tag color={'blue'} className={'ml-auto font-semibold text-xs'}>
                {ingredients.length} item{ingredients.length !== 1 ? 's' : ''}
              </Tag>
            </h3>
            <div className={'space-y-3'}>
              {ingredients.map((item, index) => (
                <div
                  key={item.productId || index}
                  className={
                    'flex items-center gap-4 '
                  }
                >
                  <div>
                    
                    <p className={'font-semibold text-gray-800'}>
                      {item.productName || '—'} :
                    </p>
                  </div>

                  <div className={'text-right'}>
                    
                    <p className={'font-bold text-blue-600'}>
                      {item.requiredQuantity ?? '—'} {item.unit || ''}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EMPTY INGREDIENTS STATE */}
        {ingredients.length === 0 && (
          <div className={'bg-white rounded-lg shadow-sm border border-primary-100 p-5 text-center text-gray-400'}>
            <UnorderedListOutlined className={'text-3xl mb-2'} />
            <p className={'text-sm'}>No ingredients listed for this production batch.</p>
          </div>
        )}

      </div>
    </Modal>
  );
};

ProductionDetails.propTypes = {
  viewModal: PropTypes.shape({
    visible: PropTypes.bool.isRequired,
    record: PropTypes.object,
  }).isRequired,
  setViewModal: PropTypes.func.isRequired,
};

export default ProductionDetails;
